<template>
    <main
        class="booking-wizard"
        :ref="(el) => initWizardBody(el as HTMLElement | undefined)"
        :data-account="[activeAccount?.code]"
    >
        <template v-if="!hasActiveAccount">
            <Banner>Account not loaded</Banner>
        </template>
        <template v-else-if="appIsReady">
            <template v-if="timetableBlocksApp">
                <component :is="layoutWrapper">
                    <div class="booking-wizard-content">
                        <div class="container">
                            <div class="steps">
                                <div class="step html-page">
                                    <div class="content">
                                        <h2>
                                            {{
                                                i18n.t('timeTable.disabled.title', {
                                                    park_name: activeAccount?.information.parkName,
                                                })
                                            }}
                                        </h2>
                                        <div v-html="i18n.t('timeTable.disabled.message')"></div>
                                        <button class="mt-15 button button-alt" @click="reloadPage">
                                            {{ i18n.t('timeTable.disabled.retry') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </component>
            </template>
            <template v-else>
                <component :is="layoutWrapper">
                    <ProgressBar v-if="usesProgressBar" />
                    <RestartWizardPopup />
                    <div class="booking-wizard-content">
                        <div class="container">
                            <Header />
                            <Steps />
                        </div>
                        <div class="container">
                            <Footer />
                        </div>
                    </div>
                </component>
            </template>
        </template>
        <template v-else>
            <AppLoader :loading="!appIsReady" />
        </template>
    </main>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import accounts from '@/widgets/Wizard/accounts';
import { matchAccountConfigThroughHostname } from '@/widgets/Wizard/modules/accounts';
import { useMetadataStore } from '@/widgets/Wizard/stores/metadata';
import { useStepsStore } from '@/widgets/Wizard/stores/steps';
import AppLoader from '@/widgets/Wizard/components/app-loader.vue';
import Banner from '@/widgets/Wizard/components/banner.vue';
import ProgressBar from '@/widgets/Wizard/components/progress-bar.vue';
import Header from '@/widgets/Wizard/components/wizard/header.vue';
import Steps from '@/widgets/Wizard/components/steps.vue';
import Footer from '@/widgets/Wizard/components/wizard/footer.vue';
import DefaultWrapper from '@/widgets/Wizard/components/wizard/default-wrapper.vue';
import { loadAccountStyles } from '@/widgets/Wizard/modules/styles';
import { useTimeTableTimeout } from '@/widgets/Wizard/composables/time-table/timeout';
import { useTimeTableBookStatus, checkTimeTableBookStatus } from '@/widgets/Wizard/composables/time-table/book-status';
import {
    useTimeTableWizardOptions,
    checkTimeTableWizardOptions,
} from '@/widgets/Wizard/composables/time-table/wizard-options';
import { useTimeTableGateway, useTommyGateway } from '@/widgets/Wizard/composables/container';
import RestartWizardPopup from '@/widgets/Wizard/components/popups/restart-wizard.vue';
import { BookingAllowedStatus } from '@/widgets/Wizard/models/time-table/status';
import { useTeleportsStore } from '@/widgets/Wizard/stores/teleports';

const i18n = useI18n();
const { locale } = i18n;

const stepsStore = useStepsStore();

const metaDataStore = useMetadataStore();
const { setTommy } = metaDataStore;

const { startTimeout } = useTimeTableTimeout();
const { updateBookStatus, activeBookStatus } = useTimeTableBookStatus();
const { activeWizardOptions, wizardOptionsChanged, updateWizardOptions, wizardOptionsChangeHandled } =
    useTimeTableWizardOptions();

const { activeAccount, wizardOptionsOverride, tommyMetaData } = storeToRefs(metaDataStore);
const { setFlows, selectFlow, initializeSteps } = stepsStore;

const teleportsStore = useTeleportsStore();

const props = defineProps({
    identifier: {
        type: String,
        required: true,
    },
    params: {
        type: Object,
        default: null,
    },
});

const isInitializing = ref(true);

const appIsReady = computed(() => {
    return isInitializing.value !== true;
});

const timetableBlocksApp = computed(() => {
    return activeBookStatus.value === BookingAllowedStatus.INACTIVE;
});

const hasActiveAccount = computed(() => {
    return activeAccount.value !== null;
});

const usesProgressBar = computed(() => {
    return activeAccount.value?.options.usesProgressBar ?? true;
});

const layoutWrapper = computed(() => {
    return activeAccount.value?.wrapper ?? DefaultWrapper;
});

async function setupTimeout() {
    const timeTable = useTimeTableGateway();

    if (!activeAccount.value) {
        return;
    }

    const account = activeAccount.value;

    const checks: Array<() => void> = [];
    const currentChecks: any[] = [];

    if (activeAccount.value.options.useTimeTable) {
        currentChecks.push(checkTimeTableBookStatus(timeTable, account));
        checks.push(async () => updateBookStatus(await checkTimeTableBookStatus(timeTable, account)));
    }

    if (activeAccount.value.options.useTimeTableWizardOptions) {
        currentChecks.push(checkTimeTableWizardOptions(timeTable, account));
        checks.push(async () => updateWizardOptions(await checkTimeTableWizardOptions(timeTable, account), true));
    }

    if (checks.length === 0) {
        return;
    }

    if (currentChecks.length > 0) {
        const results = await Promise.all(currentChecks);

        if (currentChecks.length === 1 && activeAccount.value.options.useTimeTable) {
            updateBookStatus(results[0]);
        } else if (currentChecks.length === 1 && activeAccount.value.options.useTimeTableWizardOptions) {
            updateWizardOptions(results[0], false);
        } else {
            updateBookStatus(results[0]);
            updateWizardOptions(results[1], false);
        }
    }

    startTimeout(checks);
}

async function setupMetaData() {
    const tommy = useTommyGateway();

    setTommy(tommy);

    let salesChannel = undefined;

    if (activeAccount.value && activeAccount.value.options.salesChannel) {
        salesChannel = activeAccount.value.options.salesChannel;
    }

    tommyMetaData.value = await tommy.getMetaData(salesChannel);
    await metaDataStore.updateCountries(locale.value);
}

function reloadPage() {
    window.location.reload();
}

function initWizardBody(element: HTMLElement | undefined) {
    if (element) {
        teleportsStore.updateTeleportHost('wizardBody', element);
    }
}

watch(wizardOptionsChanged, () => {
    if (!wizardOptionsChanged.value) {
        return;
    }

    if (!appIsReady.value) {
        wizardOptionsChangeHandled();
        return;
    }

    window.location.reload();
});

watch(activeWizardOptions, () => {
    wizardOptionsOverride.value = activeWizardOptions.value;
});

onBeforeMount(async () => {
    const account = matchAccountConfigThroughHostname(window.location.hostname, accounts);

    if (!account) {
        return;
    }

    // Import styling in development
    if (import.meta.env.DEV) {
        await import(/* @vite-ignore */ `./assets/css/accounts/${account.code}.scss`);
    }

    // Import styling in production
    if (import.meta.env.PROD) {
        loadAccountStyles(account);
    }
});

onBeforeMount(async () => {
    const account = matchAccountConfigThroughHostname(window.location.hostname, accounts);

    if (!account) {
        return;
    }

    activeAccount.value = account;

    if (account.options.defaultLocale) {
        locale.value = account.options.defaultLocale;
    }

    isInitializing.value = true;

    setFlows(activeAccount.value.config.flows);
    initializeSteps();

    await Promise.all([setupTimeout(), setupMetaData()]);

    isInitializing.value = false;

    // Starts the wizard
    selectFlow('default');
});
</script>

<style>
@import '@/widgets/Wizard/assets/css/tailwind.css';
</style>

<style lang="scss">
@use '@/widgets/Wizard/assets/css/main.scss';
</style>
